

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getProfile, login as loginApi, logout as logoutApi, verifyLoginOtp as verifyLoginOtpApi, type LoginResult, type LoginOtpStartResponse } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { Schemas } from '../types/api-types';
import { config } from '../config/app.config';

type AuthUser = Schemas['AuthResponse'];

export type LoginFlowResult =
    | { otp_required: false }
    | {
        otp_required: true;
        otp_token: string;
        phone_masked?: string | null;
        name?: string | null;
        user_name?: string | null;
        dev_otp?: string | null;
    };

interface AuthContextType {
    user: null | AuthUser;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<LoginFlowResult>;
    loginWithOtp: (otpToken: string, otpCode: string) => Promise<void>;
    logout: () => Promise<void>;
    fetchProfile: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<null | AuthUser>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const hasInitialized = useRef(false);
    const idleTimerRef = useRef<number | null>(null);
    const navigate = useNavigate();
    const IDLE_TIMEOUT_MS = config.idleTimeoutMin * 60 * 1000; // 15 minutes

    const activityEvents: Array<keyof DocumentEventMap> = [
        'mousemove',
        'mousedown',
        'keydown',
        'touchstart',
        'scroll',
        'click'
    ];

    const fetchProfile = useCallback(async () => {
        try {
            setLoading(true);
            const user = await getProfile();
            setIsAuthenticated(true);
            setUser(user);
        } catch (error: any) {
            // Only set unauthenticated state if error is not 401
            if (error?.response?.status !== 401) {
                setIsAuthenticated(false);
                setUser(null);
            }
            // If 401, do nothing: let refresh logic/event handle state
        } finally {
            setLoading(false);
        }
    }, []);

    // Initialize auth on mount (fetch initial profile)
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            fetchProfile();
        }
    }, [fetchProfile]);

    const login = async (username: string, password: string): Promise<LoginFlowResult> => {
        try {
            const res: LoginResult = await loginApi(username, password);
            if ((res as LoginOtpStartResponse).otp_required) {
                const otpRes = res as LoginOtpStartResponse;
                return {
                    otp_required: true,
                    otp_token: otpRes.otp_token || '',
                    phone_masked: otpRes.phone_masked ?? null,
                    name: otpRes.name ?? null,
                    user_name: otpRes.user_name ?? null,
                    dev_otp: otpRes.dev_otp ?? null
                };
            }
            await fetchProfile();
            // Reset idle timer on successful login
            resetIdleTimer();
            return { otp_required: false };
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            throw error;
        }
    };

    const loginWithOtp = async (otpToken: string, otpCode: string) => {
        try {
            await verifyLoginOtpApi(otpToken, otpCode);
            await fetchProfile();
            resetIdleTimer();
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            throw error;
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await logoutApi();
        } catch (error) {
            // Optionally handle error
        } finally {
            setUser(null);
            setIsAuthenticated(false);
            clearIdleTimer();
            setLoading(false);
            navigate('/login', { replace: true });
        }
    };

    // Idle timer helpers
    const clearIdleTimer = () => {
        try {
            if (idleTimerRef.current) {
                window.clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        } catch (e) {
            // ignore
        }
    };

    const startIdleTimer = () => {
        clearIdleTimer();
        // Use window.setTimeout so the returned id is number in TS DOM
        idleTimerRef.current = window.setTimeout(async () => {
            // perform logout on timeout
            try {
                await logout();
            } catch (e) {
                // ignore
            }
        }, IDLE_TIMEOUT_MS) as unknown as number;
    };

    const resetIdleTimer = () => {
        if (!isAuthenticated) return;
        startIdleTimer();
    };

    // Attach activity listeners when authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            clearIdleTimer();
            return;
        }

        const onActivity = () => {
            resetIdleTimer();
        };

        // start the timer initially
        startIdleTimer();

        for (const ev of activityEvents) {
            document.addEventListener(ev, onActivity, { passive: true });
        }

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                // optionally keep running timer while hidden; do nothing
            } else {
                // when coming back, reset timer
                resetIdleTimer();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearIdleTimer();
            for (const ev of activityEvents) {
                document.removeEventListener(ev, onActivity);
            }
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [isAuthenticated]);

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, loading, login, loginWithOtp, logout, fetchProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
